import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { request } from '../../helpers/apiHelper'
import SectionLoader from '../../components/sectionLoader'
import { currentUserQuery } from './queries'

// The CallTools webhook (blinker Api::V3::Webhooks::CalltoolsController) adds
// the agent id that made the call to the link it sends the agent to. The
// webhook cannot see who is logged in here, so this is where the two are
// compared. Keep the name in step with MissionControlLinks.
export const CALLTOOLS_AGENT_PARAM = 'calltoolsAgent'

type Status = 'checking' | 'match' | 'mismatch' | 'unknown'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// The API stores a UUID agent id lower-cased and everything else as sent, so
// the same rule is applied to both sides before they are compared.
const normalize = (id?: string | null) => {
  const trimmed = (id || '').trim()
  return UUID.test(trimmed) ? trimmed.toLowerCase() : trimmed
}

interface GuardProps {
  agentId: string
  children: ReactNode
}

const Guard: FunctionComponent<GuardProps> = ({ agentId, children }) => {
  const history = useHistory()
  const { pathname, search } = useLocation()
  const sessionUser = useSelector((state: any) => state.session?.user) || {}
  const currentUserId = sessionUser.id
  const currentUserName = [sessionUser.first_name, sessionUser.last_name]
    .filter(Boolean)
    .join(' ')

  const [status, setStatus] = useState<Status>('checking')
  const [ownAgentId, setOwnAgentId] = useState<string | null>(null)

  // Read from the API, not the session: the session copy is made at sign-in
  // and does not follow an agent id saved since. `currentUser` rather than
  // `user(id:)`: the latter needs the show-User permission (transactions, refis
  // or users badge), so an agent with only another badge, refi_phone say, could
  // not read their own record and would never see the warning.
  useEffect(() => {
    let cancelled = false
    // A new link or a new session must not show the previous verdict while the
    // new lookup runs.
    setStatus('checking')
    const settle = (next: Status, own: string | null = null) => {
      if (cancelled) return
      setOwnAgentId(own)
      setStatus(next)
    }

    if (!currentUserId) {
      settle('unknown')
      return () => {
        cancelled = true
      }
    }

    request(currentUserQuery, {})
      .then((response) => {
        const user = response.data?.data?.currentUser
        if (response.data?.errors || !user) {
          settle('unknown')
          return
        }
        const own = normalize(user.calltoolsAgentId)
        settle(own === normalize(agentId) ? 'match' : 'mismatch', own || null)
      })
      // A failed lookup must not stand between the agent and their call.
      .catch(() => settle('unknown'))

    return () => {
      cancelled = true
    }
  }, [agentId, currentUserId])

  if (status === 'checking') return <SectionLoader isLoading />

  if (status !== 'mismatch') return <>{children}</>

  // Same destination, minus the marker, so a refresh does not ask again.
  const continueAnyway = () => {
    const params = new URLSearchParams(search)
    params.delete(CALLTOOLS_AGENT_PARAM)
    const rest = params.toString()
    history.replace({ pathname, search: rest ? `?${rest}` : '' })
  }

  return (
    <div className="calltools-error">
      <div className="calltools-error__card" role="alert">
        <div className="calltools-error__icon" aria-hidden="true">
          !
        </div>
        <h2 className="calltools-error__title">
          This call was started by a different agent
        </h2>
        <p className="calltools-error__body">
          {currentUserName
            ? `You are signed in to MissionControl as ${currentUserName}, but`
            : 'You are signed in to MissionControl, but'}{' '}
          the call came from another CallTools agent. This call may belong to
          that agent. Check before you make changes.
        </p>
        {!ownAgentId && (
          <p className="calltools-error__body">
            Your MissionControl user has no CallTools agent ID saved.
          </p>
        )}

        <div className="calltools-error__actions">
          <button
            type="button"
            className="button button--primary-round"
            onClick={continueAnyway}
          >
            Continue anyway
          </button>
          <Link className="button button--secondary-round" to="/">
            Back to MissionControl
          </Link>
        </div>
      </div>
    </div>
  )
}

// Wraps a signed-in route. Without the marker in the link (anything that did
// not come from the CallTools webhook) it renders the route untouched.
export const CalltoolsAgentCheck: FunctionComponent = ({ children }) => {
  const { search } = useLocation()
  const agentId = new URLSearchParams(search).get(CALLTOOLS_AGENT_PARAM)

  if (!agentId || !agentId.trim()) return <>{children}</>

  return <Guard agentId={agentId}>{children}</Guard>
}

export default CalltoolsAgentCheck
